import { AFRICA_COUNTRIES } from "./connect-countries-africa";
import { ASIA_COUNTRIES } from "./connect-countries-asia";

export interface ConnectCountryFAQ {
  question: string;
  answer: string;
}

export interface ConnectCountryConfig {
  name: string;
  slug: string;
  code: string;
  dialCode: string;
  flag: string;
  capital: string;
  majorCities: string[];
  diasporaName: string;
  population: string;
  heroImage: string;
  story?: string;
  storyKey?: string;
  seoTitle: string;
  seoDescription: string;
  seoKeywords: string[];
  hasTopUp: boolean;
  faqs: ConnectCountryFAQ[];
}

const CORE_COUNTRIES: Record<string, ConnectCountryConfig> = {
  'south-sudan': {
    name: 'South Sudan',
    slug: 'south-sudan',
    code: 'SS',
    dialCode: '+211',
    flag: '🇸🇸',
    capital: 'Juba',
    heroImage: 'https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?w=800&q=80',
    majorCities: ['Juba', 'Wau', 'Malakal', 'Bor', 'Bentiu'],
    diasporaName: 'South Sudanese-Australian',
    population: '11 million',
    story: 'South Sudan is a nation of bold dreamers and resilient builders. In Juba, young entrepreneurs are launching tech startups at the Scenius Hub and Juba iHub - innovation centres where over 600 members are coding apps, building digital platforms, and creating solutions for their communities. From ICT hubs empowering youth in Jonglei State to the University of Juba\'s Innovation Centre nurturing the next generation of engineers, South Sudan\'s tech scene is rising fast. This is a nation where the world\'s youngest country meets the world\'s freshest ideas - where cattle camps and code exist side by side, and where the spirit of the White Nile fuels ambitions as vast as the open savannas.',
    seoTitle: 'Call South Sudan from Australia | elefa Connect',
    seoDescription: 'Make cheap international calls to South Sudan from Australia. Call to Juba, Wau, Malakal and all of South Sudan. No contracts, credit never expires.',
    seoKeywords: ['call south sudan australia', 'south sudan calling app', 'juba phone calls', 'cheap calls south sudan'],
    hasTopUp: false,
    faqs: [
      { question: 'How much does it cost to call South Sudan from Australia?', answer: 'elefa Connect offers competitive rates for calls to South Sudan. Check our rates page for the latest per-minute pricing to mobile and landline numbers.' },
      { question: 'What is the country code for South Sudan?', answer: 'The country code for South Sudan is +211. When dialing from Australia, you would dial +211 followed by the local number.' },
      { question: 'Can I call mobile phones in South Sudan?', answer: 'Yes, you can call both mobile phones and landlines in South Sudan. Rates may vary between mobile and landline calls.' },
      { question: 'What cities in South Sudan can I call?', answer: 'You can call any city in South Sudan including Juba, Wau, Malakal, Bor, Bentiu, Yei, and all other locations nationwide.' }
    ]
  },
  'ethiopia': {
    name: 'Ethiopia',
    slug: 'ethiopia',
    code: 'ET',
    dialCode: '+251',
    flag: '🇪🇹',
    capital: 'Addis Ababa',
    heroImage: 'https://images.unsplash.com/photo-1734865934450-719ef6f59a37?w=800&q=80',
    majorCities: ['Addis Ababa', 'Dire Dawa', 'Gondar', 'Bahir Dar', 'Hawassa'],
    diasporaName: 'Ethiopian-Australian',
    population: '120 million',
    story: 'The birthplace of coffee and one of humanity\'s oldest civilisations, Ethiopia is a land of extraordinary beauty and pride. From the rock-hewn churches of Lalibela carved into mountainsides to the ancient obelisks of Aksum, this is a nation that has never been colonised - a beacon of African independence and enduring culture.',
    seoTitle: 'Call Ethiopia from Australia | elefa Connect',
    seoDescription: 'Make cheap international calls to Ethiopia from Australia. HD quality calls to Addis Ababa, Dire Dawa, Gondar and across Ethiopia. No contracts required.',
    seoKeywords: ['call ethiopia australia', 'ethiopia calling app', 'addis ababa phone calls', 'cheap calls ethiopia'],
    hasTopUp: false,
    faqs: [
      { question: 'How much does it cost to call Ethiopia from Australia?', answer: 'elefa Connect offers competitive rates for calls to Ethiopia. Check our rates page for the latest per-minute pricing to mobile and landline numbers.' },
      { question: 'What is the country code for Ethiopia?', answer: 'The country code for Ethiopia is +251. When dialing from Australia, dial +251 followed by the local number.' },
      { question: 'What features does elefa Connect offer for Ethiopia?', answer: 'elefa Connect provides HD voice calls to all Ethiopian phone numbers. Additional services may be added in future updates.' },
      { question: 'Do I need a SIM card to use elefa Connect?', answer: 'No SIM card required. elefa Connect works over WiFi or mobile data, so you can call Ethiopia from any smartphone or tablet with an internet connection.' }
    ]
  },
  'kenya': {
    name: 'Kenya',
    slug: 'kenya',
    code: 'KE',
    dialCode: '+254',
    flag: '🇰🇪',
    capital: 'Nairobi',
    heroImage: 'https://images.unsplash.com/photo-1523805009345-7448845a9e53?w=800&q=80',
    majorCities: ['Nairobi', 'Mombasa', 'Kisumu', 'Nakuru', 'Eldoret'],
    diasporaName: 'Kenyan-Australian',
    population: '54 million',
    story: 'From the snow-capped summit of Mount Kenya to the golden plains of the Masai Mara, Kenya is where the wild heart of Africa beats strongest. Nairobi pulses as East Africa\'s innovation capital, while the ancient Swahili coast tells stories of centuries of trade, culture, and connection across oceans.',
    seoTitle: 'Call Kenya from Australia | elefa Connect',
    seoDescription: 'Make cheap international calls to Kenya from Australia. Call to Nairobi, Mombasa, Kisumu and all of Kenya. Credit never expires.',
    seoKeywords: ['call kenya australia', 'kenya calling app', 'nairobi phone calls', 'cheap calls kenya'],
    hasTopUp: false,
    faqs: [
      { question: 'How much does it cost to call Kenya from Australia?', answer: 'elefa Connect offers competitive rates for calls to Kenya. Check our rates page for the latest per-minute pricing.' },
      { question: 'What is the country code for Kenya?', answer: 'The country code for Kenya is +254. When dialing from Australia, dial +254 followed by the local number.' },
      { question: 'What features does elefa Connect offer for Kenya?', answer: 'elefa Connect provides HD voice calls to all Kenyan phone numbers. Additional services may be added in future updates.' },
      { question: 'Do I need a SIM card to use elefa Connect?', answer: 'No SIM card required. elefa Connect works over WiFi or mobile data, so you can call Kenya from any smartphone or tablet with an internet connection.' }
    ]
  },
  'uganda': {
    name: 'Uganda',
    slug: 'uganda',
    code: 'UG',
    dialCode: '+256',
    flag: '🇺🇬',
    capital: 'Kampala',
    heroImage: 'https://images.unsplash.com/photo-1619451334792-150fd785ee74?w=800&q=80',
    majorCities: ['Kampala', 'Entebbe', 'Gulu', 'Jinja', 'Mbarara'],
    diasporaName: 'Ugandan-Australian',
    population: '47 million',
    story: 'Known as the Pearl of Africa, Uganda is a land of breathtaking natural beauty - from the mist-covered Rwenzori Mountains to the source of the Nile at Jinja. Home to half the world\'s remaining mountain gorillas, Uganda offers a warmth and generosity of spirit that stays with everyone who experiences it.',
    seoTitle: 'Call Uganda from Australia | elefa Connect',
    seoDescription: 'Make cheap international calls to Uganda from Australia. HD voice calls to Kampala, Entebbe, Gulu and across Uganda. No contracts, no expiry.',
    seoKeywords: ['call uganda australia', 'uganda calling app', 'kampala phone calls', 'cheap calls uganda'],
    hasTopUp: false,
    faqs: [
      { question: 'How much does it cost to call Uganda from Australia?', answer: 'elefa Connect offers competitive rates for calls to Uganda. Check our rates page for the latest per-minute pricing.' },
      { question: 'What is the country code for Uganda?', answer: 'The country code for Uganda is +256. When dialing from Australia, dial +256 followed by the local number.' },
      { question: 'What features does elefa Connect offer for Uganda?', answer: 'elefa Connect provides HD voice calls to all Ugandan phone numbers. Additional services may be added in future updates.' },
      { question: 'Do I need a SIM card to use elefa Connect?', answer: 'No SIM card required. elefa Connect works over WiFi or mobile data, so you can call Uganda from any smartphone or tablet with an internet connection.' }
    ]
  },
  'somalia': {
    name: 'Somalia',
    slug: 'somalia',
    code: 'SO',
    dialCode: '+252',
    flag: '🇸🇴',
    capital: 'Mogadishu',
    heroImage: 'https://images.unsplash.com/photo-1611348524140-53c9a25263d6?w=800&q=80',
    majorCities: ['Mogadishu', 'Hargeisa', 'Kismayo', 'Bosaso', 'Baidoa'],
    diasporaName: 'Somali-Australian',
    population: '17 million',
    story: 'With the longest coastline in mainland Africa, Somalia is a land of poets, traders, and seafarers. From the ancient port city of Mogadishu to the highlands of Hargeisa, Somali culture is woven with powerful oral traditions, frankincense-scented history, and a diaspora that keeps its homeland close to heart.',
    seoTitle: 'Call Somalia from Australia | elefa Connect',
    seoDescription: 'Make cheap international calls to Somalia from Australia. Call to Mogadishu, Hargeisa and all of Somalia. No contracts required.',
    seoKeywords: ['call somalia australia', 'somalia calling app', 'mogadishu phone calls', 'cheap calls somalia'],
    hasTopUp: false,
    faqs: [
      { question: 'How much does it cost to call Somalia from Australia?', answer: 'elefa Connect offers competitive rates for calls to Somalia. Check our rates page for the latest per-minute pricing.' },
      { question: 'What is the country code for Somalia?', answer: 'The country code for Somalia is +252. When dialing from Australia, dial +252 followed by the local number.' },
      { question: 'Can I call Somaliland through elefa Connect?', answer: 'Yes, you can call both Somalia and Somaliland (including Hargeisa) using the +252 country code.' },
      { question: 'What features does elefa Connect offer for Somalia?', answer: 'elefa Connect provides HD voice calls to all Somali phone numbers. Additional services may be added in future updates.' }
    ]
  },
  'nigeria': {
    name: 'Nigeria',
    slug: 'nigeria',
    code: 'NG',
    dialCode: '+234',
    flag: '🇳🇬',
    capital: 'Abuja',
    heroImage: 'https://images.unsplash.com/photo-1618828665347-d870c38c95c7?w=800&q=80',
    majorCities: ['Lagos', 'Abuja', 'Kano', 'Ibadan', 'Port Harcourt'],
    diasporaName: 'Nigerian-Australian',
    population: '220 million',
    story: 'Africa\'s most populous nation and its largest economy, Nigeria is a powerhouse of creativity, enterprise, and culture. From the electric energy of Lagos to the ancient walls of Kano, from Nollywood\'s global storytelling to Afrobeats rhythms that move the world - Nigeria\'s influence is felt everywhere.',
    seoTitle: 'Call Nigeria from Australia | elefa Connect',
    seoDescription: 'Make cheap international calls to Nigeria from Australia. HD voice calls to Lagos, Abuja, Kano and across Nigeria. Credit never expires.',
    seoKeywords: ['call nigeria australia', 'nigeria calling app', 'lagos phone calls', 'cheap calls nigeria'],
    hasTopUp: false,
    faqs: [
      { question: 'How much does it cost to call Nigeria from Australia?', answer: 'elefa Connect offers competitive rates for calls to Nigeria. Check our rates page for the latest per-minute pricing.' },
      { question: 'What is the country code for Nigeria?', answer: 'The country code for Nigeria is +234. When dialing from Australia, dial +234 followed by the local number.' },
      { question: 'What features does elefa Connect offer for Nigeria?', answer: 'elefa Connect provides HD voice calls to all Nigerian phone numbers. Additional services may be added in future updates.' },
      { question: 'Do I need a SIM card to use elefa Connect?', answer: 'No SIM card required. elefa Connect works over WiFi or mobile data, so you can call Nigeria from any smartphone or tablet with an internet connection.' }
    ]
  }
};

// Merge core + expanded African countries
export const CONNECT_COUNTRIES: Record<string, ConnectCountryConfig> = {
  ...CORE_COUNTRIES,
  ...AFRICA_COUNTRIES,
  ...ASIA_COUNTRIES,
};

export const POPULAR_COUNTRIES = [
  'south-sudan', 'ethiopia', 'nigeria', 'kenya', 'uganda', 'somalia',
  'ghana', 'tanzania', 'egypt', 'south-africa', 'dr-congo', 'cameroon'
];

// Build reverse lookup maps from CONNECT_COUNTRIES
const codeToSlugMap: Record<string, string> = {};
const dialCodeToSlugMap: Record<string, string> = {};
const nameToSlugMap: Record<string, string> = {};

for (const [slug, config] of Object.entries(CONNECT_COUNTRIES)) {
  codeToSlugMap[config.code] = slug;
  const cleanDial = config.dialCode.replace('+', '');
  dialCodeToSlugMap[cleanDial] = slug;
  nameToSlugMap[config.name.toLowerCase()] = slug;
}

export function getCountryByCode(code: string): ConnectCountryConfig | undefined {
  const slug = codeToSlugMap[code.toUpperCase()];
  return slug ? CONNECT_COUNTRIES[slug] : undefined;
}

export function getCountrySlugByCode(code: string): string {
  return codeToSlugMap[code.toUpperCase()] || code.toLowerCase();
}

/**
 * Get country slug from dial code (e.g., "211" -> "south-sudan")
 */
export function getCountrySlugByDialCode(dialCode: string): string | null {
  const code = dialCode.replace('+', '');
  return dialCodeToSlugMap[code] || null;
}

/**
 * Get country slug from country name (e.g., "South Sudan" -> "south-sudan")
 */
export function getCountrySlugByName(name: string): string | null {
  return nameToSlugMap[name.toLowerCase()] || null;
}

/**
 * Generate a URL-safe slug from any country name.
 * e.g. "Burkina Faso" -> "burkina-faso", "Côte d'Ivoire" -> "cote-divoire"
 */
export function generateSlugFromName(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // strip diacritics
    .replace(/['']/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}
